/**
 * ChatContext — provides chat state + unread polling shared by the floating
 * chat icon (badge) and the ChatPanel UI.
 *
 * Polls /api/chat/unread on the same 10s loop used by NotificationContext.
 * Exposes:
 *   totalUnread, unreadByPeer
 *   openPanel(peerId?), closePanel(), isOpen, activePeerId
 *   refreshUnread()
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 10_000;

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadByPeer, setUnreadByPeer] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [activePeerId, setActivePeerId] = useState(null);
  const prevTotalRef = useRef(0);
  const initRef = useRef(false);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/chat/unread`);
      const t = r.data?.total_unread || 0;
      const map = r.data?.by_peer || {};
      // Soft ping when a new DM arrives (skip on first load)
      if (initRef.current && t > prevTotalRef.current) {
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) {
            const ctx = new Ctx();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = 660;
            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
            osc.connect(gain).connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.35);
            setTimeout(() => ctx.close(), 500);
          }
        } catch (err) { log.debug('[chat] audio ping blocked:', err?.message || err); }
      }
      prevTotalRef.current = t;
      initRef.current = true;
      setTotalUnread(t);
      setUnreadByPeer(map);
    } catch (err) {
      // 401 handled by global axios interceptor
      log.warn('[chat] unread poll failed:', err?.message || err);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setTotalUnread(0); setUnreadByPeer({}); setIsOpen(false); setActivePeerId(null);
      prevTotalRef.current = 0; initRef.current = false;
      return undefined;
    }
    refreshUnread();
    const id = setInterval(refreshUnread, POLL_MS);
    return () => clearInterval(id);
  }, [user, refreshUnread]);

  const openPanel = useCallback((peerId = null) => {
    setIsOpen(true);
    if (peerId) setActivePeerId(peerId);
  }, []);
  const closePanel = useCallback(() => {
    setIsOpen(false);
    setActivePeerId(null);
  }, []);

  const value = {
    totalUnread, unreadByPeer, isOpen, activePeerId,
    openPanel, closePanel, refreshUnread, setActivePeerId,
  };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
};
